import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MdCheckCircle, MdOutlineWarningAmber } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { POINT } from "../../config/apiConfig";
import { Badge } from "../crm/Primitives";
import { formatMoney } from "../crm/crmHelpers";
import {
  canChargeAgain,
  describePointPayment,
  isPointChargeActive,
  isPointChargeDone,
  isPointChargeSettling,
  POINT_STATUS_META,
  shouldPollPointCharge,
} from "./eventHelpers";

/*
  Acompanhamento de uma cobrança de plano na maquininha.

  A tela só mostra e pergunta: quem diz que foi pago é o Mercado Pago, e quem
  libera o plano é o servidor. Aqui a equipe vê em que passo a cobrança está e o
  que fazer a seguir.

  Consulta o servidor a cada 2,5 s enquanto o estado ainda vai mudar sozinho
  (em andamento, ou aprovada e ainda liberando o plano). Parou de mudar, para de
  consultar. Uma falha de rede na consulta NÃO derruba o que já está na tela: a
  última situação conhecida fica, com um aviso discreto.

  Não bloqueia fechar a janela: a cobrança continua na maquininha e o plano é
  liberado quando a cliente pagar, com a janela aberta ou não.
*/

const POLL_MS = 2500;

const HELP = {
  CREATING: "Enviando o valor para a maquininha. Isso leva alguns segundos.",
  ACTION_REQUIRED:
    "Confira na maquininha se o pagamento foi aprovado ou recusado. Esta tela atualiza sozinha.",
  REJECTED: "O cartão foi recusado. Peça outro cartão ou tente de novo.",
  CANCELED: "A cobrança foi cancelada e a maquininha está livre.",
  EXPIRED: "Passou o tempo sem a cliente pagar. A maquininha está livre.",
  FAILED:
    "Confira se a maquininha está ligada, em modo PDV e conectada à internet, e tente de novo.",
  REVIEW:
    "O dinheiro entrou, mas o pagamento não bate com este pedido (por exemplo, chegou depois de cancelar, ou o pedido já tinha sido decidido). O plano NÃO foi liberado. Fale com um administrador para conferir e estornar se for o caso.",
  REFUNDED: "O pagamento foi estornado e o plano não foi liberado.",
};

const PointChargePanel = ({ charge, onChange, onRetry, onDone }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const doneRef = useRef(false);

  const query = useQuery(
    ["events", "point-charge", charge.id],
    async () => {
      const { data } = await axios.get(`${POINT.CHARGE}/${charge.id}`);
      return data.data;
    },
    {
      initialData: charge,
      // Dado inicial vale como velho: consulta já ao abrir, para a tela não
      // mostrar um estado de minutos atrás como se fosse atual.
      initialDataUpdatedAt: 0,
      cacheTime: 0,
      refetchInterval: (data) => (shouldPollPointCharge(data) ? POLL_MS : false),
      refetchIntervalInBackground: true,
      onSuccess: (data) => onChange?.(data),
    }
  );

  const current = query.data || charge;

  const cancel = useMutation(
    () => axios.post(`${POINT.CHARGE}/${current.id}/cancel`),
    {
      onSuccess: ({ data }) => {
        setError(null);
        queryClient.setQueryData(["events", "point-charge", current.id], data.data);
        onChange?.(data.data);
      },
      onError: (err) =>
        setError(
          err.response?.data?.message || "Não foi possível cancelar agora. Tente de novo."
        ),
    }
  );

  // Terminou bem: avisa quem abriu o painel uma única vez.
  useEffect(() => {
    if (isPointChargeDone(current) && !doneRef.current) {
      doneRef.current = true;
      onDone?.(current);
    }
  }, [current, onDone]);

  const meta = POINT_STATUS_META[current.status] || {
    label: current.status,
    tone: "neutral",
  };
  const active = isPointChargeActive(current);
  const settling = isPointChargeSettling(current);
  const done = isPointChargeDone(current);
  const canCancel =
    (current.status === "CREATED" || current.status === "AT_TERMINAL") &&
    !current.cancelRequested;

  const help = (() => {
    if (done) return `${describePointPayment(current)}. O plano já foi liberado.`;
    if (settling) return "Pagamento aprovado. Liberando o plano...";
    if (current.status === "CREATED" || current.status === "AT_TERMINAL") {
      return `O valor aparece na maquininha${
        current.terminalName ? ` ${current.terminalName}` : ""
      }. Peça para a cliente passar o cartão.${
        current.requestedType === "CREDIT"
          ? " No crédito, ela escolhe as parcelas na própria maquininha."
          : ""
      }`;
    }
    return HELP[current.status] || "";
  })();

  return (
    <div className="space-y-3" aria-live="polite">
      <div
        className={`rounded-2xl p-4 ring-1 ring-inset ${
          done
            ? "bg-emerald-50 ring-emerald-200"
            : current.status === "REVIEW" || current.status === "REJECTED"
            ? "bg-rose-50 ring-rose-200"
            : "bg-slate-50 ring-slate-200"
        }`}
      >
        <div className="flex items-center gap-2">
          {active || settling ? (
            <span className="relative flex h-3 w-3" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75 motion-reduce:animate-none" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-500" />
            </span>
          ) : done ? (
            <MdCheckCircle size={20} className="text-emerald-600" />
          ) : current.status === "REVIEW" ? (
            <MdOutlineWarningAmber size={20} className="text-rose-600" />
          ) : null}
          <Badge tone={settling ? "info" : meta.tone}>
            {settling ? "Liberando o plano" : meta.label}
          </Badge>
        </div>

        <p className="mt-2 text-3xl font-bold leading-none text-slate-900">
          {formatMoney(current.amount)}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          {current.requestedType === "CREDIT" ? "Cartão de crédito" : "Cartão de débito"}
          {current.terminalName ? ` · ${current.terminalName}` : ""}
        </p>

        {help ? <p className="mt-3 text-sm text-slate-700">{help}</p> : null}

        {current.cancelRequested && active ? (
          <p className="mt-2 text-xs font-semibold text-amber-700">
            Cancelamento pedido. Aguardando a maquininha confirmar; se a cliente
            pagar nesse intervalo, o pagamento vale.
          </p>
        ) : null}
      </div>

      {query.isError && shouldPollPointCharge(current) ? (
        <p className="text-[11px] font-semibold text-amber-700">
          Sem resposta do servidor agora. Tentando de novo.
        </p>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800 ring-1 ring-inset ring-rose-200"
        >
          {error}
        </p>
      ) : null}

      {active || settling ? (
        <p className="text-[11px] text-slate-500">
          Pode fechar esta janela: a cobrança continua e o plano é liberado assim
          que a cliente pagar.
        </p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row">
        {canCancel ? (
          <button
            type="button"
            onClick={() => cancel.mutate()}
            disabled={cancel.isLoading}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
          >
            {cancel.isLoading ? "Cancelando..." : "Cancelar cobrança"}
          </button>
        ) : null}

        {canChargeAgain(current) ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-xl bg-pink-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-pink-700"
          >
            Cobrar de novo
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default PointChargePanel;
